import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import {
  HrailRailTerminal,
  InsertHrailRailTerminalBody,
  UpdateHrailRailTerminalBody,
} from "./types/railTerminals";

export const useHrailRailTerminalsApi = () => {
  const api = useApi();

  const getAdminHrailRailTerminal = useCallback(
    (railTerminalId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/rail_terminals/${railTerminalId}`;
      return useSWRMutation<HrailRailTerminal, CustomError, string>(url, (url) =>
        api.get<HrailRailTerminal>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminHrailRailTerminals = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_terminals`, query);
      return useSWRImmutable<Paginated<HrailRailTerminal>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailRailTerminal>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailRailTerminals = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/rail_terminals`, query);
      return api.get<Paginated<HrailRailTerminal>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailRailTerminal = useCallback(
    async (body: InsertHrailRailTerminalBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailRailTerminalBody>(
        `/admin/settings/hrail/rail_terminals`,
        body,
      );
      mutateCache([`/admin/settings/hrail/rail_terminals`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailRailTerminal = useCallback(
    async (railTerminalId: string, body: UpdateHrailRailTerminalBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_terminals/${railTerminalId}`;
      await api.put<UpdateHrailRailTerminalBody>(url, body);
      mutateCache([`/admin/settings/hrail/rail_terminals`], url);
    },
    [api],
  );

  const updateAdminHrailRailTerminalVisibility = useCallback(
    async (railTerminalId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_terminals/${railTerminalId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/rail_terminals`]);
    },
    [api],
  );

  const updateAdminHrailRailTerminalOrder = useCallback(
    async (railTerminalId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/rail_terminals/${railTerminalId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/rail_terminals`]);
    },
    [api],
  );

  const deleteAdminHrailRailTerminal = useCallback(
    async (railTerminalId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/rail_terminals/${railTerminalId}`);
      mutateCache([`/admin/settings/hrail/rail_terminals`]);
    },
    [api],
  );

  return {
    getAdminHrailRailTerminal,
    useAdminHrailRailTerminals,
    getAdminHrailRailTerminals,
    insertAdminHrailRailTerminal,
    updateAdminHrailRailTerminal,
    updateAdminHrailRailTerminalVisibility,
    updateAdminHrailRailTerminalOrder,
    deleteAdminHrailRailTerminal,
  };
};
